import TextButton from 'LuxKit/Button/TextButton'
import Divider from 'LuxKit/Divider'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Caption from 'LuxKit/Typography/Caption'
import Pane from 'components/Common/Pane'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import {
  CountdownChip,
  StepIndicatorColumn,
  StepCircle,
  StepLine,
  StepContent,
  StepItem,
} from 'loyalty/components/StatusMatchShared'
import useMyStatusMatchEnrollment from 'loyalty/hooks/useMyStatusMatchEnrollment'
import { formatTimeRemaining } from 'loyalty/statusMatch/formatTimeRemaining'
import { resolveTemplate } from 'loyalty/statusMatch/resolveTemplate'
import { useAppSelector } from 'hooks/reduxHooks'
import { rem } from 'polished'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { tierGradientBackgroundStyle, tierNormalContrastColorStyle } from 'styles/utils/tierStyles'

const GradientCard = styled.div`
  padding: ${rem(20)} ${rem(24)};
  border-radius: ${props => props.theme.borderRadius.L};
  ${tierGradientBackgroundStyle}
  ${tierNormalContrastColorStyle}
`

const PageCard = styled(Pane)`
  padding: ${rem(24)};
`

const CardContent = styled.div`
  position: relative;
  z-index: 1;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${rem(12)};
  margin-bottom: ${rem(4)};
`

const ButtonRight = styled.div`
  display: flex;
  justify-content: flex-end;
`

/**
 * Renders a Status Match challenge card for the user's active enrollment.
 *
 * Variants:
 * - page: white Pane card (Benefits & Progress) — title, subtitle, steps, divider, CTA
 * - drawer: compact tier gradient card — title, campaign description, "View" link
 *
 * Data flow:
 * - Title/subtitle: Lab challengeCard templates resolved with enrollment.metadata
 * - Steps: Lab successSteps merged with BE checks by key
 * - Countdown: enrollment.expiresAt formatted via formatTimeRemaining
 *
 * Returns null if there's no active campaign OR the user isn't enrolled.
 */
interface Props {
  /** 'page' = white bg (Benefits & Progress), 'drawer' = tier gradient (account drawer) */
  variant?: 'page' | 'drawer'
  /** Called when a navigation link is clicked — used by the drawer to close itself */
  onLinkClick?: () => void
}

function StatusMatchChallengeCard({ variant = 'page', onLinkClick }: Props) {
  const intl = useIntl()
  const { enrollment, config, steps, isEnrolled } = useMyStatusMatchEnrollment()
  const regionCode = useAppSelector(state => state.geo.currentRegionCode)

  // Build template variables from enrollment metadata + formatted dates
  const templateVars = useMemo(() => {
    if (!enrollment) return {}
    const metadata = enrollment.metadata ?? {}
    const vars: Record<string, string> = {}
    for (const [key, value] of Object.entries(metadata)) {
      if (typeof value === 'string') vars[key] = value
    }
    if (enrollment.expiresAt) {
      vars.expiresDate = intl.formatDate(enrollment.expiresAt, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    }
    return vars
  }, [enrollment, intl])

  if (!enrollment || !isEnrolled) return null

  const challengeCard = config?.challengeCard
  const timeRemaining = formatTimeRemaining(enrollment.expiresAt, intl)
  const browseOffersUrl = `/${regionCode}/search?category=lux_exclusive`

  // Resolve display text from Lab templates or fall back to defaults
  const cardTitle = challengeCard?.cardTitle
    ? resolveTemplate(challengeCard.cardTitle, templateVars)
    : enrollment.campaignName

  const fallbackSubtitle = intl.formatMessage({
    id: 'KXcndHWDsevvKRAVd3ry',
    defaultMessage: 'Complete the steps below to unlock your matched status',
    description: 'Subtitle on the status match challenge card explaining what the user needs to do',
  })

  const cardSubtitle = challengeCard?.cardSubtitle
    ? resolveTemplate(challengeCard.cardSubtitle, templateVars)
    : fallbackSubtitle

  const browseOffersLabel = intl.formatMessage({
    id: 'axOyEP1a-x_5Yk60Wqwo',
    defaultMessage: 'Browse eligible offers',
    description: 'Button text on the status match challenge card linking to eligible offers search page',
  })

  const stepsContent = steps.length > 0 && <div>
    {steps.map((step, index) => (
      <StepItem key={step.key}>
        <StepIndicatorColumn>
          <StepCircle eligible={step.eligible}>
            {step.eligible && <LineCheckIcon size="XXS" colour="neutral-eight" />}
          </StepCircle>
          {index < steps.length - 1 && <StepLine />}
        </StepIndicatorColumn>
        <StepContent>
          <BodyText variant="large" weight="bold">{step.title}</BodyText>
          <BodyText variant="large" colour="neutral-two">{step.description}</BodyText>
        </StepContent>
      </StepItem>
    ))}
  </div>

  // Drawer: compact layout — campaign name + description
  if (variant === 'drawer') {
    const tier = templateVars.targetTier?.toLowerCase() ?? 'silver'
    return <GradientCard className={`tier-${tier}`}>
      <CardContent>
        <VerticalSpacer gap={8}>
          <Header>
            <VerticalSpacer gap={4}>
              {timeRemaining && <CountdownChip>{timeRemaining}</CountdownChip>}
              <Heading variant="heading5">{cardTitle}</Heading>
            </VerticalSpacer>
            <TextButton kind="tertiary" size="small" to="/societe" onClick={onLinkClick}>
              {intl.formatMessage({
                id: 'YCbv2_DThINXVBNxlhKW',
                defaultMessage: 'View',
                description: 'Link text on the status match challenge card to view the account page',
              })}
            </TextButton>
          </Header>
          {enrollment.campaignDescription && <Caption variant="medium" colour="neutral-two">{enrollment.campaignDescription}</Caption>}
        </VerticalSpacer>
      </CardContent>
    </GradientCard>
  }

  // Page: full layout — title/subtitle left, countdown chip right, steps, divider, CTA
  return <PageCard>
    <VerticalSpacer gap={16}>
      <Header>
        <VerticalSpacer gap={4}>
          <Heading variant="heading3">{cardTitle}</Heading>
          <BodyText variant="large" colour="neutral-two">{cardSubtitle}</BodyText>
        </VerticalSpacer>
        {timeRemaining && <CountdownChip>{timeRemaining}</CountdownChip>}
      </Header>
      {stepsContent}
      <Divider kind="primary" />
      <ButtonRight>
        <TextButton kind="primary" size="large" to={browseOffersUrl}>
          {browseOffersLabel}
        </TextButton>
      </ButtonRight>
    </VerticalSpacer>
  </PageCard>
}

export default StatusMatchChallengeCard
